// Entry credit — the switch, and the one place the feature is visible
// while it is off.
//
// WHY THIS LIVES HERE AND NOT ON A USER ROW
//
// Everything else about entry credit disappears when the feature is
// switched off: the buyer's balance, the API fields, the account page
// section, the panel on each user in the Users tab. A feature that is
// present but unusable teaches people to ignore parts of the interface.
//
// So exactly one screen shows it while off, and it is this one — behind
// manage_payment_providers, the same permission that governs every other
// decision about taking money. Whoever can turn it on can see it; nobody
// else has any reason to know it exists.
//
// THE ACKNOWLEDGEMENT IS NOT A FORMALITY. Holding customer money in
// advance may be regulated activity in the UK, and as of this writing
// nobody qualified has been asked. The server refuses to enable credits
// without it and records who confirmed and when. Ticking it is an
// assertion by the operator, not a dialog to dismiss.
import React, { useEffect, useState } from "react";
import { showToast } from "./toast.js";
import { siteDateTime } from "./siteTime.js";

// The same treatment AdminApp's ViewOnlyGate applies: a disabled fieldset
// at reduced opacity. Mirrored rather than imported because ViewOnlyGate
// is defined inside AdminApp.jsx and not exported — and consistency here
// matters more than avoiding six lines of duplication, since a panel that
// looks live while everything around it is greyed out reads as a bug.
//
// The fieldset does the disabling on its own, so the per-control disabled
// props are belt and braces rather than the mechanism.
function Frozen({ frozen, title, children }) {
  if (!frozen) return children;
  return (
    <fieldset disabled title={title} style={{ border: "none", padding: 0, margin: 0 }}>
      <div style={{ opacity: 0.55 }}>{children}</div>
    </fieldset>
  );
}

export default function EntryCreditSettingsPanel({ token, wsRequest, canEdit, modeStatus }) {
  // FROZEN IN ANY MODE BUT LIVE, like every other settings panel.
  //
  // paidEntryMode is a Live setting; the backend refuses to change it from
  // Test or Demo anyway (updateSiteSettings asserts Live). Without this the
  // controls stayed clickable under a banner saying the data was frozen,
  // which invites a save that can only fail.
  // DEMO ONLY, not "anything but Live".
  //
  // These are SETTINGS, and freezing them in Test mode meant switching to
  // Live to save a form and switching back to carry on testing — friction
  // with little behind it. Provider credentials already have separate
  // test and live slots, and the data on screen is gated by mode anyway.
  //
  // Demo stays frozen: root pulls it as a brake to show somebody the
  // product, and settings changing under a demonstration is a real
  // surprise. The server enforces the same rule in
  // assertSettingsEditable.
  const frozen = (modeStatus?.effectiveMode || "live") === "demo";
  const frozenTitle = "Leave Demo mode to manage this";
  const locked = !canEdit || frozen;
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const load = () => {
    wsRequest("admin.siteSettings.private", {}, token)
      .then((data) => {
        setSettings(data);
      })
      .catch((err) => {
        setLoadFailed(true);
        showToast(err.message || "Could not reach backend.", "error");
      });
  };

  useEffect(load, []);

  // "Enabled" here means CREDIT IS FOR SALE, not "the feature is on" — the
  // feature is always on since 1.261.0. The name is unchanged only because
  // it is read in a dozen places in the JSX below; every label around it
  // says which of the two it means.
  const enabled = settings?.paidEntryMode === "credits";

  const setMode = async (mode) => {
    setBusy(true);
    try {
      const payload = { paidEntryMode: mode };
      // Only sent when turning it ON. The server ignores it otherwise, and
      // sending it unconditionally would make the tick look like a thing
      // that travels with every save rather than a one-time assertion.
      if (mode === "credits") payload.acknowledgeStoredValue = acknowledged;
      await wsRequest("admin.siteSettings.update", payload, token);
      showToast(
        mode === "credits"
          ? "Buyers can now buy entry credit."
          : "Entry credit is no longer for sale. Buyers still earn and spend it."
      );
      setAcknowledged(false);
      load();
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    } finally {
      setBusy(false);
    }
  };

  // loadFailed, not the old `error` state: the message now goes to a
  // toast, but this still has to stop showing "Loading…" for ever when the
  // fetch fails.
  if (!settings && !loadFailed) return <p>Loading…</p>;

  return (
    // NO WRAPPER OF ITS OWN, and no width cap. This renders inside a
    // SettingsCard in the Competition settings grid (1.265.0), and the
    // grid decides how wide a card is — maxWidth: 640 here made this one
    // card refuse to match its neighbours. The heading is the card's
    // title now; only the On/Off badge stays, because a card title cannot
    // carry one.
    <div>
      <Frozen frozen={frozen} title={frozenTitle}>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <span
          style={{
            fontSize: 12,
            padding: "2px 8px",
            borderRadius: 10,
            background: enabled ? "var(--accent-blue)" : "var(--bg-input)",
            color: enabled ? "#fff" : "var(--text-primary)",
            opacity: enabled ? 1 : 0.7,
          }}
        >
          {enabled ? "On" : "Off"}
        </span>
      </div>

      <p style={{ fontSize: 13, opacity: 0.8, marginTop: 0 }}>
        Whether a buyer can BUY entry credit with money. That is all this controls.
      </p>
      <p style={{ fontSize: 13, opacity: 0.8 }}>
        Entry credit itself always works: buyers earn it from buying pixels and spend it on
        entries beyond their free allowance, whatever this is set to. With it off, a buyer who
        runs out has one route to more — buying pixels.
      </p>
      <p style={{ fontSize: 12, opacity: 0.65, marginTop: 0 }}>
        Until 1.261.0 this turned the whole feature off, and the panel said so. It did not
        mean that: a buyer could be refused an entry they held the credit for.
      </p>

      {!enabled ? (
        <div
          style={{
            border: "1px solid var(--border-color)",
            borderRadius: 8,
            padding: 14,
            marginTop: 12,
          }}
        >
          <p style={{ fontSize: 13, marginTop: 0 }}>
            <strong>Before switching this on.</strong> Holding customer money in advance is
            different from taking payment for something delivered immediately. In the UK it can
            approach regulated territory — stored value and e-money rules, and consumer rules
            about unspent balances and refunds. This has not been checked by anyone qualified.
            See <code>docs/paid-entries.md</code>.
          </p>
          <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13 }}>
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              disabled={locked || busy}
              title={frozen ? frozenTitle : undefined}
              style={{ marginTop: 3 }}
            />
            <span>
              I have taken appropriate advice, and I accept responsibility for operating this
              site's entry-credit balances.
            </span>
          </label>
          <button
            type="button"
            onClick={() => setMode("credits")}
            disabled={locked || busy || !acknowledged}
            title={frozen ? frozenTitle : undefined}
            style={{ marginTop: 12 }}
          >
            {busy ? "Working…" : "Let buyers buy credit"}
          </button>
          <div style={{ fontSize: 11, opacity: 0.55, marginTop: 8 }}>
            Your account and the time are recorded against this.
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 12 }}>
          {settings.paidEntryAcknowledgedBy && (
            <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 12 }}>
              Enabled by {settings.paidEntryAcknowledgedBy}
              {settings.paidEntryAcknowledgedAt
                ? ` on ${siteDateTime(settings.paidEntryAcknowledgedAt)}`
                : ""}
            </div>
          )}

          {/* THE "LARGEST SINGLE TOP-UP" CONTROL WAS HERE, AND IT DID
              NOTHING FOR TWO DAYS.

              maxCreditTopUp was removed from the backend on 5 September
              2026 with the move to credits. This control kept reading it
              (always undefined, so always 0) and kept saving it — and
              updateSiteSettings destructures a FIXED FIELD LIST and
              silently ignores anything it does not name, so every Save
              reported success and changed nothing.

              A ceiling is still worth having; it guarded a free-text
              amount against a slipped decimal point. It has to come back
              as a CREDIT ceiling beside minCreditTopUpCredits, with the
              backend accepting the field, and not before. Removed in
              1.266.0. */}

          <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid var(--border-color)" }}>
            <button type="button" onClick={() => setMode("off")} disabled={locked || busy} title={frozen ? frozenTitle : undefined}>
              Stop selling credit
            </button>
            {/* CORRECTED IN 1.265.0. This said balances "become unspendable
                and invisible until this is switched on again", which was
                true of the old meaning and false since 1.261.0: balances
                stay readable and still pay for entries. Missed by the
                1.262.0 wording sweep because it sits below the fold of
                this panel, in the branch that only renders while credit is
                already on. */}
            <div style={{ fontSize: 11, opacity: 0.55, marginTop: 8 }}>
              Balances are kept and stay spendable — buyers can still pay for entries with
              credit they hold, and still earn more by buying pixels. Only buying credit
              stops.
            </div>
          </div>
        </div>
      )}
      </Frozen>
    </div>
  );
}
